import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { React, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Colors from '../../colors';
import Button from '../../components/Button';
import ButtonOutline from '../../components/ButtonOutline';
import LoadingSpinner from '../../components/LoadingSpinner';
import MemberRow from '../../components/MemberRow';
import SearchBar from '../../components/SearchBar';
import TextInput from '../../components/TextInput';
import AddMemberModal from '../../components/modals/AddMemberModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import EditMemberModal from '../../components/modals/EditMemberModal';
import {
    createNewSubcollectionDocInDb, deleteDocInSubCollection, deleteDocumentAndSubCollection, getAllDocsBySubCollection,
    getDocByCollectionAndId, updateDocByCollectionAndId, updateDocInSubCollection
} from '../../firebase';
import EncourageCreation from '../../components/EncourageCreation';

function EditTeam() {
    const [teamName, setTeamName] = useState('');
    const [teamMembers, setTeamMembers] = useState([]);
    const [memberRows, setMemberRows] = useState([]);

    // For 'Add Member' modal
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
    const openAddMemberModal = () => setAddMemberModalVisible(true);
    const closeAddMemberModal = () => setAddMemberModalVisible(false);

    // For 'Edit Member' modal
    const [editMemberModalVisible, setEditMemberModalVisible] = useState(false);
    const [editedMember, setEditedMember] = useState();
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [editPhoneNumber, setEditPhoneNumber] = useState('');
    const [editNotes, setEditNotes] = useState('');

    const [deleteConfirmationModalIsVisible,
        setDeleteConfirmationModalIsVisible] = useState(false);

    // Enables Loading Spinner
    const [isLoading, setIsLoading] = useState(true);

    const openEditMemberModal = (member) => {
        setEditedMember(member);
        setEditFirstName(member.firstName);
        setEditLastName(member.lastName);
        setEditPhoneNumber(member.phoneNumber);
        setEditNotes(member.notes);
        setEditMemberModalVisible(true);
    };
    const closeEditMemberModal = () => setEditMemberModalVisible(false);

    // For Search Bar
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const navigate = useNavigate();

    const { teamId } = useParams();

    useEffect(() => {
        const fetchData = async () => {
            const teamData = await getDocByCollectionAndId('Teams', teamId);
            setTeamName(teamData?.name);
            const membersArray = await getAllDocsBySubCollection('Teams', teamId, 'Members');
            setTeamMembers(membersArray);
        };
        fetchData();
    }, [teamId]);

    // Create MemberRows whenever team members are updated
    useEffect(() => {
        setMemberRows([]);

        teamMembers.sort((memberA, memberB) => {
            const firstNameA = memberA.firstName.toLowerCase();
            const firstNameB = memberB.firstName.toLowerCase();

            if (firstNameA < firstNameB) {
                return -1; // a should be before b
            } else if (firstNameA > firstNameB) {
                return 1; // a should be after b
            }

            return 0; // a and b are equal in terms of firstName
        });

        for (const member of teamMembers.sort()) {
            const memberRow = <MemberRow
                key={member.firstName + member.lastName}
                member={member}
                onClickEdit={() => openEditMemberModal(member)} />
            setMemberRows(prevArray => [...prevArray, memberRow]);
        };
    }, [teamMembers]);

    // Use effect for filtering displayed rows by search
    useEffect(() => {
        setSearchResults([]);
        if (search.length > 0) {
            setSearchResults(memberRows.filter((memberRow) => {
                return (
                    memberRow
                        .props
                        .member
                        .firstName
                        .toLowerCase()
                        .includes(search.toLowerCase()) ||
                    memberRow
                        .props
                        .member
                        .lastName
                        .toLowerCase()
                        .includes(search.toLowerCase())
                );
            }));
        }
        setIsLoading(false);
    }, [search, memberRows]);

    // Manually add new member using 'Add Member' button
    async function addMember() {
        const newMemberData = {
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            notes: notes
        };
        closeAddMemberModal();
        setIsLoading(true);

        const newMember = await createNewSubcollectionDocInDb('Teams', teamId, 'Members', newMemberData);
        setTeamMembers(prevArray => [...prevArray, newMember]);
        setIsLoading(false);

        // Reset the initial member states
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setNotes('');
    };

    // Edit member
    async function editMember() {
        const newMember = {
            id: editedMember.id,
            firstName: editFirstName,
            lastName: editLastName,
            phoneNumber: editPhoneNumber,
            notes: editNotes
        }
        closeEditMemberModal();
        setIsLoading(true);
        setTeamMembers(teamMembers.filter(member => member !== editedMember));
        setTeamMembers(prevArray => [...prevArray, newMember]);
        await updateDocInSubCollection('Teams', teamId, 'Members', newMember.id, newMember);
        setIsLoading(false);

        // Reset the edit states
        setEditFirstName('');
        setEditLastName('');
        setEditPhoneNumber('');
        setEditNotes('');
    };

    // Delete member
    async function deleteMember() {
        closeEditMemberModal();
        setIsLoading(true);
        setTeamMembers(teamMembers.filter(member => member !== editedMember));
        await deleteDocInSubCollection('Teams', teamId, 'Members', editedMember.id);
        setIsLoading(false);
    };

    // Update team in db
    const updateTeam = async () => {
        setIsLoading(true);
        await updateDocByCollectionAndId('Teams', teamId, { name: teamName });
        setIsLoading(false);
        navigate(`/teams`);
    };

    // Delete team by removing it from db
    const onClickDeleteTeam = async () => {
        await deleteDocumentAndSubCollection('Teams', teamId, 'Members', teamMembers.length > 0)
        navigate(`/teams`);
    };

    // Displays correct member rows or message
    function displayMemberRows() {
        let displayedMemberRows;
        if (searchResults.length === 0 && memberRows.length === 0) {
            displayedMemberRows = <EncourageCreation headerText={'(0) Members found.'} suggestionText={'Tap the add button to add a new member.'} />;
        } else if (search.length === 0 && memberRows.length > 0) {
            displayedMemberRows = memberRows;
        } else if (searchResults.length === 0 && memberRows.length > 0) {
            displayedMemberRows = 'No members match search.';
        } else {
            displayedMemberRows = searchResults;
        }
        return displayedMemberRows;
    }

    const MemberRowContainer = styled('div')(() => ({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
    }));

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '15px',
            }}>

            {isLoading && <LoadingSpinner isLoading={isLoading} />}

            <h2>Edit Team</h2>

            <div style={{ width: '20rem' }}>
                <TextInput
                    label={'Team Name'}
                    value={teamName}
                    setText={setTeamName} />
            </div>

            <SearchBar
                placeholder={'Search Member'}
                search={search}
                setSearch={setSearch} />

            <MemberRowContainer>
                {displayMemberRows()}
            </MemberRowContainer>

            <Box
                sx={{
                    marginTop: '2%',
                    marginBottom: '2%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px',
                }} >
                <ButtonOutline
                    color={Colors.blue}
                    onClick={openAddMemberModal}>Add Member</ButtonOutline>

                <AddMemberModal
                    label={'Add Member'}
                    isVisible={addMemberModalVisible}
                    setIsVisible={setAddMemberModalVisible}
                    setFirstNameText={setFirstName}
                    setLastNameText={setLastName}
                    setPhoneNumText={setPhoneNumber}
                    setNotesText={setNotes}
                    text={notes}
                    onClickAddMember={addMember}
                    firstName={firstName}
                    lastName={lastName}
                    phoneNumber={phoneNumber} />

                <EditMemberModal
                    label={'Add Member'}
                    isVisible={editMemberModalVisible}
                    setIsVisible={setEditMemberModalVisible}
                    setFirstNameText={setEditFirstName}
                    setLastNameText={setEditLastName}
                    setPhoneNumText={setEditPhoneNumber}
                    setNotesText={setEditNotes}
                    firstNameValue={editFirstName}
                    lastNameValue={editLastName}
                    phoneNumberValue={editPhoneNumber}
                    notesValue={editNotes}
                    onClickSave={editMember}
                    onClickDelete={deleteMember}
                    firstName={editFirstName}
                    lastName={editLastName} />

                {teamName?.length === 0 || memberRows?.length === 0
                    ? <Button
                        color={Colors.grey2}>
                        Save Changes
                    </Button>
                    : <Button
                        color={Colors.green}
                        onClick={updateTeam}>
                        Save Changes
                    </Button>
                }

                <Button
                    color={Colors.red}
                    onClick={() => setDeleteConfirmationModalIsVisible(true)}>
                    Delete Team
                </Button>
            </Box>
            <ConfirmationModal
                isVisible={deleteConfirmationModalIsVisible}
                setIsVisible={setDeleteConfirmationModalIsVisible}
                mainAction={'Delete'}
                mainActionHandler={onClickDeleteTeam}
                title={'Are you sure you want to delete this team? All team member data will be lost.'} />
        </div>
    );
}

export default EditTeam;